import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getWallet, getTreatBundles, getShopItems, purchaseItem, getInventory, getUnlocks } from '../api/client';
import { getCosmeticComponent } from '../components/cosmetics';
import { getPetComponent } from '../components/pets';
import { PetVisual, CosmeticVisual } from '../components/visuals';
import { getFoodComponent } from '../components/food';
import { useTebexCheckout } from '../hooks/useTebexCheckout';
import TreatHistory from '../components/treats/TreatHistory';
import './Shop.css';

const CATEGORY_TABS = [
  { id: 'all', label: 'All' },
  { id: 'package', label: 'Packages' },
  { id: 'food', label: 'Food' },
  { id: 'consumable', label: 'Items' },
  { id: 'ability', label: 'Abilities' },
  { id: 'pet', label: 'Pets' },
  { id: 'cosmetic', label: 'Cosmetics' },
  { id: 'emote', label: 'Emotes' },
];

const CATEGORY_ICONS = {
  package: '📦', food: '🍖', consumable: '🧪', ability: '⭐', pet: '🐾', cosmetic: '👒', emote: '🎭',
};

export default function Shop() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { buyPackage, isLoading: tebexLoading, error: tebexError, clearError: clearTebexError } = useTebexCheckout();
  const [treats, setTreats] = useState(0);
  const [bundles, setBundles] = useState([]);
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [purchasing, setPurchasing] = useState(null);
  const [message, setMessage] = useState('');
  const [showFree, setShowFree] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingBundle, setPendingBundle] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    getWallet().then(r => setTreats(r.data.treats)).catch(() => {});
    getTreatBundles().then(r => setBundles(r.data.bundles || [])).catch(() => {});
    getShopItems().then(r => setItems(r.data.items || [])).catch(() => {});
    getInventory().then(r => setInventory(r.data.items || [])).catch(() => {});
    getUnlocks().then(r => setUnlocks(r.data.unlocks || [])).catch(() => {});
  }, [isAuthenticated]);

  // Surface the return state from Tebex redirect, then clean the URL
  useEffect(() => {
    const purchase = searchParams.get('purchase');
    if (purchase === 'success') {
      setMessage('Payment complete! Treats will appear within a moment.');
      getWallet().then(r => setTreats(r.data.treats)).catch(() => {});
    } else if (purchase === 'cancelled') {
      setMessage('Checkout cancelled.');
    }
    if (purchase) {
      const next = new URLSearchParams(searchParams);
      next.delete('purchase');
      setSearchParams(next, { replace: true });
      setTimeout(() => setMessage(''), 5000);
    }
  }, [searchParams, setSearchParams]);

  const handleBuyBundle = async (bundle) => {
    clearTebexError();
    if (!bundle.tebex_package_id) {
      setMessage('This bundle is not available yet.');
      setTimeout(() => setMessage(''), 4000);
      return;
    }
    setPendingBundle(bundle.id);
    try {
      await buyPackage(bundle.tebex_package_id);
    } finally {
      setPendingBundle(null);
    }
  };

  const filtered = items
    .filter(i => activeTab === 'all' || i.category === activeTab)
    .filter(i => showFree || i.price_treats > 0)
    .filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase())
                          || i.description.toLowerCase().includes(search.toLowerCase()));

  const handlePurchase = async (itemId) => {
    setPurchasing(itemId);
    try {
      const res = await purchaseItem(itemId);
      if (res.data.success) {
        setTreats(res.data.treats_remaining);
        setMessage(`Purchased ${res.data.item?.name || itemId}!`);
        // Refresh inventory and unlocks
        getInventory().then(r => setInventory(r.data.items || [])).catch(() => {});
        getUnlocks().then(r => setUnlocks(r.data.unlocks || [])).catch(() => {});
      } else {
        setMessage(res.data.message);
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Purchase failed.');
    }
    setPurchasing(null);
    setTimeout(() => setMessage(''), 4000);
  };

  const getOwnedQty = (itemId) => {
    // Check unlocks for pets, cosmetics, abilities, packages
    if (unlocks.includes(itemId)) return 1;
    // Check inventory for consumables and food
    const inv = inventory.find(i => i.item_id === itemId);
    return inv ? (inv.uses_remaining ?? inv.quantity) : 0;
  };

  if (authLoading) return <div className="shop-loading">Loading...</div>;

  return (
    <div className="shop-page">
      <div className="container">
        <div className="shop-header">
          <h1>Shop</h1>
          <div className="shop-wallet">
            <span className="wallet-icon">🦴</span>
            <span className="wallet-amount">{treats}</span>
            <span className="wallet-label">Treats</span>
          </div>
        </div>

        {message && <div className="shop-message">{message}</div>}
        {tebexError && <div className="shop-message shop-message-error">{tebexError}</div>}

        {/* Treat grant history (auto-hides if backend endpoint not live yet) */}
        <TreatHistory limit={6} />

        {/* Treat Bundles */}
        <section className="shop-section">
          <h2>Get Treats</h2>
          <div className="bundle-grid">
            {bundles.map(b => {
              const busy = tebexLoading && pendingBundle === b.id;
              return (
                <div key={b.id} className="bundle-card card">
                  <div className="bundle-treats">🦴 {b.treats}</div>
                  <div className="bundle-name">{b.name}</div>
                  <div className="bundle-desc">{b.description}</div>
                  <button
                    className="btn btn-primary btn-sm bundle-buy"
                    onClick={() => handleBuyBundle(b)}
                    disabled={busy || tebexLoading}
                  >
                    {busy ? 'Opening…' : `$${b.price_usd.toFixed(2)}`}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Shop Items */}
        <section className="shop-section">
          <h2>Spend Treats</h2>
          <div className="shop-tabs">
            {CATEGORY_TABS.map(tab => (
              <button
                key={tab.id}
                className={`shop-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            <input
              type="text"
              className="shop-search"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="shop-free-toggle">
              <input
                type="checkbox"
                checked={showFree}
                onChange={(e) => setShowFree(e.target.checked)}
              />
              Show Free
            </label>
          </div>

          <div className="shop-items-grid">
            {filtered.map(item => {
              const owned = getOwnedQty(item.id);
              const isFree = item.price_treats === 0;
              const cleanId = item.id.replace('unlock_', '');

              const handleItemClick = () => {
                if (!isFree) return;
                // Navigate to relevant page based on category
                if (item.category === 'pet') {
                  navigate('/catalog');
                } else if (item.category === 'cosmetic') {
                  // Go to first pet's cosmetics page
                  navigate('/pets/0/cosmetics');
                } else if (item.category === 'food') {
                  navigate('/pets/0');
                }
              };

              return (
                <div
                  key={item.id}
                  className={`shop-item card ${owned > 0 ? 'shop-item-owned' : ''} ${isFree ? 'shop-item-free' : ''}`}
                  onClick={isFree ? handleItemClick : undefined}
                  style={isFree ? { cursor: 'pointer' } : undefined}
                >
                  <div className="shop-item-icon">
                    {(() => {
                      if (item.category === 'cosmetic') {
                        if (getCosmeticComponent(cleanId)) return <CosmeticVisual cosmeticId={cleanId} size={40} />;
                      }
                      if (item.category === 'pet') {
                        if (getPetComponent(cleanId)) return <PetVisual petId={cleanId} size={40} />;
                      }
                      if (item.category === 'food') {
                        const F = getFoodComponent(item.id);
                        if (F) return <F size={36} />;
                      }
                      return CATEGORY_ICONS[item.category] || '🎁';
                    })()}
                  </div>
                  <h3 className="shop-item-name">{item.name}</h3>
                  <p className="shop-item-desc">{item.description}</p>
                  {item.tier && (item.category === 'pet' || item.category === 'cosmetic' || item.category === 'package') && (
                    <span className={`shop-item-tier tier-${item.tier}`}>{item.tier}</span>
                  )}
                  {item.uses && <span className="shop-item-uses">{item.uses} uses</span>}
                  {owned > 0 && <span className="shop-item-owned-badge">Owned ({owned})</span>}
                  <div className="shop-item-footer">
                    {isFree ? (
                      <>
                        <span className="shop-item-price shop-item-price-free">Free</span>
                        <span className="btn btn-ghost btn-sm">View &rarr;</span>
                      </>
                    ) : owned > 0 && (item.category === 'pet' || item.category === 'cosmetic' || item.category === 'ability' || item.category === 'package') ? (
                      <>
                        <span className="shop-item-price shop-item-price-free">Owned</span>
                      </>
                    ) : (
                      <>
                        <span className="shop-item-price">🦴 {item.price_treats}</span>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => { e.stopPropagation(); handlePurchase(item.id); }}
                          disabled={purchasing === item.id || treats < item.price_treats}
                        >
                          {purchasing === item.id ? '...' : 'Buy'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
